import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Shield, Lock, Smartphone, Key, CheckCircle, AlertTriangle,
  Eye, EyeOff, Copy, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecuritySettings = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [setting2FA, setSetting2FA] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    checkTwoFactorStatus();
  }, []);

  const checkTwoFactorStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers });
      setTwoFactorEnabled(res.data.two_factor_enabled || false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "New passwords don't match", variant: "destructive" });
      return;
    }

    if (newPassword.length < 8) {
      toast({ title: "Error", description: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers });

      toast({ title: "Success", description: "Password changed successfully" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast({ 
        title: "Error", 
        description: e.response?.data?.detail || "Failed to change password", 
        variant: "destructive" 
      });
    }
    setChangingPassword(false);
  };

  const setup2FA = async () => {
    setSetting2FA(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers });
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
    } catch (e) {
      toast({ title: "Error", description: "Failed to setup 2FA", variant: "destructive" });
    }
    setSetting2FA(false);
  };

  const verify2FA = async () => {
    try {
      const res = await axios.post(`${API}/auth/2fa/verify`, {
        code: verificationCode
      }, { headers });

      setBackupCodes(res.data.backup_codes || []);
      setShowBackupCodes(true);
      setTwoFactorEnabled(true);
      setQrCode(null);
      setSecret("");
      setVerificationCode("");
      
      toast({ title: "Success", description: "2FA enabled successfully!" });
    } catch (e) {
      toast({ 
        title: "Error", 
        description: e.response?.data?.detail || "Invalid verification code", 
        variant: "destructive" 
      });
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast({ title: "Copied", description: "Secret key copied to clipboard" });
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast({ title: "Copied", description: "Backup codes copied to clipboard" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Header />

      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8">
        <div className="container mx-auto px-6">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="h-8 w-8" />
            Security Settings
          </h1>
          <p className="opacity-90 mt-1">Manage your account security and authentication</p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Security Status */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Security Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Password Set</p>
              </div>
              <div className={`text-center p-4 rounded-lg ${twoFactorEnabled ? 'bg-green-50' : 'bg-amber-50'}`}>
                {twoFactorEnabled ? (
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                ) : (
                  <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                )}
                <p className="text-sm font-medium">2FA {twoFactorEnabled ? 'Enabled' : 'Disabled'}</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Email Verified</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Session Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Use a strong password with at least 8 characters, including uppercase, lowercase, and numbers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
            </div>
            <div>
              <Label>New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </div>
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="w-full"
            >
              {changingPassword ? "Changing..." : "Change Password"}
            </Button>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Two-Factor Authentication (2FA)
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account using an authenticator app
                </CardDescription>
              </div>
              <Badge variant={twoFactorEnabled ? "default" : "secondary"}>
                {twoFactorEnabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {!twoFactorEnabled && !qrCode && (
              <div className="text-center py-8">
                <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Protect Your Account</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Enable two-factor authentication to add an extra layer of security. 
                  You'll need an authenticator app like Google Authenticator or Authy.
                </p>
                <Button onClick={setup2FA} disabled={setting2FA}>
                  {setting2FA ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Setting up...</>
                  ) : (
                    <><Key className="h-4 w-4 mr-2" /> Enable 2FA</>
                  )}
                </Button>
              </div>
            )}

            {qrCode && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="font-semibold mb-4">Scan QR Code</h3>
                  <div className="inline-block p-4 bg-white rounded-lg shadow-lg">
                    <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="text-sm text-gray-500">Or enter this secret manually:</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 p-2 bg-white rounded border text-sm font-mono break-all">
                      {secret}
                    </code>
                    <Button size="sm" variant="outline" onClick={copySecret}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Enter verification code from your app</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                    />
                    <Button onClick={verify2FA} disabled={verificationCode.length !== 6}>
                      Verify & Enable
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {twoFactorEnabled && !showBackupCodes && (
              <div className="text-center py-4">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-green-700">2FA is Active</h3>
                <p className="text-gray-500 mt-2">Your account is protected with two-factor authentication</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backup Codes */}
        {showBackupCodes && backupCodes.length > 0 && (
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-5 w-5" />
                Save Your Backup Codes
              </CardTitle>
              <CardDescription className="text-amber-700">
                Store these codes securely. Each code can only be used once if you lose access to your authenticator.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {backupCodes.map((code, idx) => (
                  <code key={idx} className="p-2 bg-white rounded text-center font-mono text-sm">
                    {code}
                  </code>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={copyBackupCodes} className="flex-1">
                  <Copy className="h-4 w-4 mr-2" /> Copy All
                </Button>
                <Button onClick={() => setShowBackupCodes(false)} className="flex-1">
                  I've Saved These Codes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Security Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Use a unique password that you don't use on other websites</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Enable 2FA for maximum account protection</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Never share your password or 2FA codes with anyone</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Store your backup codes in a secure location</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Log out of shared or public devices</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecuritySettings;
